import { useI18N } from "common/hooks";
import { Notices } from "common/types";
import EmptyMapperNotes from "@/assets/images/empty-mappernotes.svg";
import { useState } from "react";
import { CaretDown } from "phosphor-react";
import { XIcon } from "lucide-react";
import { useDarkMode } from "@/hooks/useDarkMode";

type NoticesView = {
  notices: Notices[];
  onClose?: () => void;
};

function NoticesView({ notices, onClose }: NoticesView) {
  const i18n = useI18N();
  const noticeCount = notices.filter(n => n.type === "Notice").length;
  const initiativeCount = notices.filter(n => n.type === "Initiative").length;
  
  // Set default tab based on available content
  const defaultTab = noticeCount > 0 ? "notice" : initiativeCount > 0 ? "initiative" : "notice";
  
  const [activeTab, setActiveTab] = useState<"all" | "notice" | "initiative">(defaultTab);
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const isDark = useDarkMode();
  
  const filteredNotices = notices.filter(notice => {
    if (activeTab === "all") return true;
    return notice.type.toLowerCase() === activeTab;
  });
  
  // Sort notices by date
  const sortedNotices = [...filteredNotices].sort((a, b) => {
    const dateA = new Date(a.start_date).getTime();
    const dateB = new Date(b.start_date).getTime();
    return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
  });
  
  const formatedDate = (date: string) => {
    if (!date) return "No end date";
    const d = new Date(date);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).split('/').join('/');
  };
  
  return (
    <section 
      className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(100vh-150px)] md:h-[calc(100vh-200px)] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Header */}
      <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 vsticky top-0">
        <h1
          id="modal-title"
          className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
        >
          {i18n.get("announcementTitle")}
        </h1>
        <button
          onClick={onClose}
          className="absolute top-4 left-4"
          aria-label="Close modal"
        >
          <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
        </button>
      </header>
      
      {/* Content */}
      <div 
        aria-label="Modal Content"
        className="flex-grow overflow-y-auto py-3 px-3 md:px-6 md:py-4"
        style={{ minHeight: '350px' }}
      >
        {/* Tabs as toggle */}
        <div className="mb-4 mt-2">
          <div className="flex bg-white dark:bg-primary-900 p-1 rounded-full border border-primary-100 dark:border-primary-800">
            <button
              className={`flex-1 py-2 px-2 md:px-4 rounded-full text-center transition-all text-sm md:text-base ${
                activeTab === "notice" 
                  ? "bg-secondary-400 text-primary-900 dark:text-primary-900 shadow-sm" 
                  : "text-primary-900 dark:text-primary-150"
              }`}
              onClick={() => setActiveTab("notice")}
            >
              Notice ({noticeCount})
            </button>
            <button
              className={`flex-1 py-2 px-2 md:px-4 rounded-full text-center transition-all text-sm md:text-base ${
                activeTab === "initiative" 
                  ? "bg-secondary-400 text-primary-900 dark:text-primary-900 shadow-sm" 
                  : "text-primary-900 dark:text-primary-150"
              }`}
              onClick={() => setActiveTab("initiative")}
            >
              Initiative ({initiativeCount})
            </button>
          </div>
        </div>
        
        {/* Sort dropdown */}
        <div className="flex justify-end mb-3">
          <button 
            className="flex items-center text-xs text-primary-800 dark:text-primary-150"
            onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
          >
            Date added {sortOrder === "newest" ? "(Newest)" : "(Oldest)"}
            <CaretDown size={14} className="ml-1" />
          </button>
        </div>
        
        {/* Notice Items Container - Fixed height to prevent layout shifts */}
        <div className="overflow-y-auto" style={{ minHeight: '300px' }}>
          {sortedNotices.length > 0 ? (
            <div className="space-y-4 pb-4">
              {sortedNotices.map((notice, index) => (
                <div key={index} className="mb-4 p-3 md:p-4 border border-primary-200 dark:border-primary-700 rounded-xl bg-white dark:bg-primary-800">
                  <h3 className="text-primary-800 dark:text-primary-150 paragraph-medium-semi-bold mb-1">
                    {notice.title}
                  </h3>
                  
                  {notice.sub_title && (
                    <p className="text-primary-800 dark:text-primary-150 paragraph-small-medium mb-1">
                      {notice.sub_title}
                    </p>
                  )}
                  
                  <p className="self-stretch justify-center text-neutral-700 dark:text-primary-300 paragraph-xsmall-medium mb-1">
                    From : {formatedDate(notice.start_date)} - {notice.end_date ? formatedDate(notice.end_date) : "No end date"}
                  </p>
                  
                  {/* Show location if exists */}
                  {(notice as any).location_name && (
                    <p className="text-sm text-primary-800 dark:text-primary-150 paragraph-small-semibold mb-2">
                      Location : {(notice as any).location_name}
                    </p>
                  )}
                  
                  <div className="self-stretch justify-center text-neutral-700 dark:text-primary-300 paragraph-xsmall-medium mb-1">
                    {formatedDate((notice as any).created_at || notice.start_date)} • Sociability team
                  </div>
                  
                  <p className="paragraph-small-medium text-primary-800 dark:text-primary-150">
                    {notice.description}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 h-full" style={{ minHeight: '250px' }}>
              <img
                src={EmptyMapperNotes}
                alt="No notices"
                className="max-w-[50%] mx-auto mb-6"
              />
              <p className="text-center paragraph-medium-semi-bold text-primary-800 dark:text-primary-150">
                No {activeTab === "notice" ? "notices" : activeTab === "initiative" ? "initiatives" : "announcements"} for this venue
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default NoticesView;
